/**
 * テーマ色（#191、docs/77、docs/10 §4.7）：<html data-theme="indigo|blue|teal|green|sumi"> を付ける1か所。色の値は styles.css の [data-theme] だけ。
 *   決まる順は 人の設定（Me.preferences.themeColor）→ 製品の既定（PRODUCTS[*].brand.theme：現場力＝藍、営業力＝濃い青）。
 *   ログイン前の画面（ログイン・会員登録・確認・再設定・退会の取り消し・招待・製品をまたぐ切替の確認）は人の設定を当てない＝製品の既定
 *   （端末に前の人のテーマを残さない。端末の Cookie にも覚えない）。
 *   起動の最初に製品の既定を付け（main.tsx）、App の ThemeHost が ME と開いている道から決め直す（描く前＝useLayoutEffect）。
 *   React に依らない（購読は useSyncExternalStore 用の subscribe）
 * 明るさ（#191 の2回目、docs/77 §8）：<html data-mode="light|dark"> も同じ所で付ける。
 *   人の設定（Me.preferences.colorMode）＝ライト（既定）／ダーク／端末に合わせる（prefers-color-scheme。OS の設定が変われば追従）。
 *   ライトのまま：ログイン前の画面（isPreloginPath）。帳票のプレビュー（紙の画面）・帳票デザインのエディタ・作成画面の「できあがりの紙」は
 *   紙そのものだけライト（左のメニュー・上の帯・机はダーク。10/16 夜 射場さん、docs/77 §8.4 ヨ）＝紙の枠 .paperpage は白・エディタの紙と
 *   できあがりの紙・地図は components/LightArea.tsx。ダークでは紙の影を消して罫線 #3A3935 で縁取る（styles.css）
 *   ブラウザの上の帯の色（<meta name="theme-color">）も紙の色に合わせる
 */
import { PRODUCTS, ThemeColor, type Me, type Product, type ResolvedColorMode } from "@genbaryoku/contract";

/**
 * 人の設定を当てない道（ログイン前の画面）。ログインしたままでも、ここでは製品の既定。
 * /handoff（製品をまたぐ会社の切替の確認）も入れる（#194）：このドメインにログインが無いか、別の人がログイン中の画面＝その人の色を当てない
 */
const PRELOGIN_PATHS: ReadonlySet<string> = new Set(["/login", "/signup", "/signup/sent", "/verify", "/forgot", "/reset-password", "/reopen", "/invite", "/handoff"]);
export function isPreloginPath(pathname: string): boolean {
  // メールの閲覧ページ（/d/<鍵>、#225）も受け取る人の画面＝製品の既定・ライト（送った会社でログイン中の人が開いても）
  if (pathname.startsWith("/d/")) return true;
  return PRELOGIN_PATHS.has(pathname.replace(/\/+$/, "") || "/");
}

/** 製品の既定の色 */
export function productTheme(p: Product): ThemeColor {
  return PRODUCTS[p].brand.theme;
}

/** いまの道と ME から当てる色（ME が無い・ログイン前の画面は、ログイン前の製品の既定） */
export function themeFor(me: Me | null | undefined, pathname: string, prelogin: Product): ThemeColor {
  if (!me || isPreloginPath(pathname)) return productTheme(prelogin);
  return me.preferences?.themeColor ?? productTheme(me.tenant.product);
}

const listeners = new Set<() => void>();
/** useSyncExternalStore 用 */
export function subscribeTheme(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
/** いま当たっている色 */
export function currentTheme(): ThemeColor {
  const v = ThemeColor.safeParse(document.documentElement.dataset.theme);
  return v.success ? v.data : "indigo";
}
/** 色を当てる（同じなら何もしない） */
export function applyTheme(t: ThemeColor): void {
  if (document.documentElement.dataset.theme === t) return;
  document.documentElement.dataset.theme = t;
  listeners.forEach((fn) => fn());
}

// ---------------------------------------------------------------------------
// 明るさ（#191 の2回目）
// ---------------------------------------------------------------------------

const DARK_QUERY = "(prefers-color-scheme: dark)";
/** 端末（OS）がダークか */
export function systemDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}
/** 端末の明るさが変わったら呼ぶ（useSyncExternalStore 用） */
export function subscribeSystemDark(fn: () => void): () => void {
  if (typeof window.matchMedia !== "function") return () => {};
  const mq = window.matchMedia(DARK_QUERY);
  mq.addEventListener("change", fn);
  return () => mq.removeEventListener("change", fn);
}

/** いまの道と ME と端末から当てる明るさ（人の設定が無ければライト） */
export function modeFor(me: Me | null | undefined, pathname: string, sysDark: boolean): ResolvedColorMode {
  if (!me || isPreloginPath(pathname)) return "light";
  const m = me.preferences?.colorMode ?? "light";
  if (m === "system") return sysDark ? "dark" : "light";
  return m;
}

const modeListeners = new Set<() => void>();
export function subscribeMode(fn: () => void): () => void {
  modeListeners.add(fn);
  return () => modeListeners.delete(fn);
}
/** いま当たっている明るさ */
export function currentMode(): ResolvedColorMode {
  return document.documentElement.dataset.mode === "dark" ? "dark" : "light";
}
/** 明るさを当てる（同じなら何もしない）。ブラウザの上の帯の色も紙の色に */
export function applyMode(m: ResolvedColorMode): void {
  if (document.documentElement.dataset.mode === m) return;
  document.documentElement.dataset.mode = m;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", m === "dark" ? "#1E1E1C" : "#FFFFFF");
  modeListeners.forEach((fn) => fn());
}
